import { Divider, Tooltip } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent } from "vue";
import {
    createFieldCheckbox,
    createFieldDate,
    createFieldInput,
    createFieldInputNumber,
    createFieldInputPassword,
    createFieldRadioGroup,
    createFieldSelect,
    createFieldTemplateBuilder, createFieldTextArea
} from "../../../components/input/template-builder/template-builder.logic";
import { has } from "../../../utils/menu-context";
import {
    createColumnImageUrl,
    createColumnInput,
    createColumnInputNumber
} from "../../../components/table/c-table.logic";
import i18n from "../../../i18n";
import store from "../../../store";
import { conditionFormChecker, putField } from "../../../utils/form";
import { createField as createFieldNameUser } from "../modal/user-modal/user-modal.logic";
import { createForm as createFormAddress } from "../../../components/address/address.logic";
import { callRegisterRole } from "../../../views/app/user-request/request-register/user-request-register.section";

const Address = defineAsyncComponent(() => import("../../../components/address/Address"));
const CIcon = defineAsyncComponent(() => import("../../../components/icon/CIcon"));
const CardBorder = defineAsyncComponent(() => import("../../../components/card/CardBorder"));
const TemplateBuilder = defineAsyncComponent(() => import("../../../components/input/template-builder/TemplateBuilder"));

export const callPersonType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "PERSON_TYPE", value: searchQuery });
export const callCompanyType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "COMPANY_TYPE", value: searchQuery });
export const callNationality = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "NATIONALITY", value: searchQuery });

export const callDepartment = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/department/cacheFindAllMain", {
        where: [{ name: "nameTh", operator: "like", value: searchQuery }],
        type: null,
    });
export const callSubDepartment = ({ searchQuery, parentId }) =>
    store.dispatch("resource/v1/master/department/cacheFindAllByParent", {
        parentId,
        where: [{ name: "nameTh", operator: "like", value: searchQuery }],
    });

export const callRace = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "RACE", value: searchQuery });
export const callGender = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "GENDER", value: searchQuery });
export const callEducation = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "EDUCATION", value: searchQuery });
export const callMainOccupation = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "MAIN_OCCUPATION", value: searchQuery });
export const callSecondOccupation = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "SECOND_OCCUPATION", value: searchQuery });
export const callJobPosition = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "JOB_POSITION", value: searchQuery });
export const callSameAddressType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "SAME_ADDRESS_TYPE", value: searchQuery });
export const callUserRequestStatus = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "USER_REQUEST_STATUS", value: searchQuery });

export const callUser = ({ searchQuery }) => {
    const where = [{ name: "isActive", type: "Boolean", operator: "=", value: true }];
    if (searchQuery) {
        where.push({ name: "fullNameTh", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/security/user/search", {
            criteria: {
                select: ["id", "version", "fullNameTh"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "fullNameTh", type: "ASC" }],
                children: []
            }
        })
        .then(page => page.data);
};
export const callRole = ({ searchQuery }) =>
    store
        .dispatch("resource/v1/security/role/cacheFindAllIsDefaultFalse", { searchQuery })
        .then((items) => items.filter((item) => !["DEV_ADMIN", "DOF_ADMIN", "DEV_API"].includes(item.name)))
        .then((items) => items.map((item) => ({ refRole: item })));

export const callCheckIdTax = ({ idTax }) => store.dispatch("resource/v1/security/user/checkIdTax", { idTax });
export const callCheckIdCard = ({ idCard }) => store.dispatch("resource/v1/security/user/checkIdCard", { idCard });
export const callRegisterAttachment = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "REGISTER_ATTACHMENT", value: searchQuery });

export const createColumns = () => {
    const columns = [
        createColumnImageUrl({ title: () => i18n.global.t("model.user.refImage"), dataIndex: "refImage.url" }),
        createColumnInput({ title: () => i18n.global.t("model.user.username"), dataIndex: "username" }),
        createColumnInput({ title: () => i18n.global.t("model.user.fullNameTh"), dataIndex: "fullNameTh", width: 200 }),
        createColumnInputNumber({
            title: () => i18n.global.t("model.user.idCard"),
            dataIndex: "idCard",
            comma: false,
            inputProp: () => ({ comma: false }),
        }),
        createColumnInput({ title: () => i18n.global.t("model.user.email"), dataIndex: "email" }),
    ];
    return columns;
};

export const createField = ({ props, data, key, isLoading }) => {
    return putField({
        data,
        fields: [...createFieldRegister({ props, data, key, isLoading })],
    });
};

export const createFieldRegister = ({ props, data, key, isLoading }) => {
    const { viewOnly, menuContext } = props;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            ...createFieldRegister1({ props, data, key, isLoading }),
            ...createFieldRegisterUserInfo({ props, data, key, isLoading }),
            ...createFieldUsernamePassword({ props, data, key, isLoading }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => <Divider></Divider>,
            },
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => (
                    <Address
                        menuContext={menuContext}
                        viewOnly={viewOnly}
                        dataProp={data}
                        dataKey={`${prefix}addresses.0`}
                        isLoading={isLoading.value}
                    ></Address>
                ),
            },
            {
                visible: () => data.useForm.modelRef?.refPersonType?.key === "1",
                colProp: () => ({ xs: 24 }),
                formItemRender: () => <Divider></Divider>,
            },
        ],
    });
};

export const createFieldRegister1 = ({ props, data, key }) => {
    const { viewOnly, menuContext } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldRadioGroup({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refPersonType`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 20 } }),
                label: () => i18n.global.t("model.user.refPersonType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refPersonType,
                            "onUpdate:value": (value) => {
                                const address = model.addresses[0];
                                model.refPersonType = value;
                                model.idCard = null;
                                model.refCompanyType = null;
                                model.companyNameTh = null;
                                model.registerDate = null;
                                model.registerCapital = null;
                                model.attachments = [];
                                model.refGender = data.genderTypes.find((d) => d.key === "M");

                                lodash.remove(model.attachmentIdCards, (d) => !d.id);
                                model.attachmentIdCards.forEach((d) => (d.isDelete = true));
                                lodash.remove(model.attachmentSignatures, (d) => !d.id);
                                model.attachmentSignatures.forEach((d) => (d.isDelete = true));

                                if (value?.key === "1") {
                                    model.refGender = null;
                                    model.refTitle = null;
                                    model.birthDate = null;
                                    model.firstNameTh = null;
                                    model.lastNameTh = null;
                                    model.refNationality = null;
                                    model.refRace = null;
                                    model.expireDate = null;
                                    model.idLine = null;
                                    model.refMainOccupation = null;
                                    model.refSecondOccupation = null;
                                    model.refEducation = null;
                                    data.useForm.clearValidate("idCard");
                                    address.isValue1 = true;
                                    if (!has({ menuContext, key: "canApprove" })) {
                                        data.registerAttachments = data.callRegisterAttachments;
                                        data.registerAttachments.forEach((item) =>
                                            model.attachments.push({
                                                keyAttachment: item.key,
                                                documentName: item.value,
                                                attribute: lodash.cloneDeep(item.attribute),
                                            })
                                        );
                                    }
                                    data.addressPersonType = address.refAddressType?.value2;
                                } else if (value?.key === "2") {
                                    data.linkIdCard = null;
                                    model.attachmentIdCards.unshift({});
                                    model.attachmentSignatures.unshift({});
                                    address.isValue1 = false;
                                    data.addressPersonType = address.refAddressType?.value;
                                }
                            },
                            disabled: viewOnly,
                            list: callPersonType,
                            optLabel: "value",
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) },
            }),
        ],
    });
};

export const createFieldRegisterUserInfo = ({ props, data, key, isLoading }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        ...createFieldNameUser({ props, data, key }),
                    ],
                    skeleton: isLoading.value,
                })
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: putField({ data, fields: [
                        createFieldDate({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}birthDate`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.birthDateRegister"),
                            labelRender: () => (
                                <span>
                                    {iconCheckBirthDate({ data, model })} {i18n.global.t("model.user.birthDateRegister")}
                                </span>
                            ),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    { value: model.birthDate, "onUpdate:value": (value) => (model.birthDate = value), disabled: viewOnly },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                        }),
                        createFieldDate({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}expireDate`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.expireDateRegister"),
                            labelRender: () => (
                                <span>
                                    {iconCheckExpireDate({ data, model })} {i18n.global.t("model.user.expireDateRegister")}
                                </span>
                            ),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    { value: model.expireDate, "onUpdate:value": (value) => (model.expireDate = value), disabled: viewOnly },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                        }),
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}email`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.email"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.email,
                                        "onUpdate:value": (value) => {
                                            model.email = value;
                                        },
                                        disabled: viewOnly,
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                        }),
                        createFieldInputNumber({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}mobile`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.mobile"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.mobile,
                                        "onUpdate:value": (value) => (model.mobile = value),
                                        comma: false,
                                        disabled: viewOnly,
                                        maxDecimal: 10,
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                        }),
                    ]}),
                    skeleton: isLoading.value,
                }),
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: putField({ data, fields: [
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}positionCode`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.positionCode"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.positionCode,
                                        "onUpdate:value": (value) => (model.positionCode = value),
                                        disabled: viewOnly,
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                        }),
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refPosition`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.refPosition"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refPosition,
                                        "onUpdate:value": (value) => (model.refPosition = value),
                                        disabled: viewOnly,
                                        list: callJobPosition,
                                        optLabel: "value",
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) },
                        }),
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refDepartment`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("views.register.refDepartment"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refDepartment,
                                        "onUpdate:value": (value) => {
                                            model.refDepartment = value;
                                            model.refSubDepartment = null;
                                        },
                                        disabled: viewOnly,
                                        list: callDepartment,
                                        optLabel: "nameTh",
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) },
                        }),
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refSubDepartment`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("views.register.refSubDepartment"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refSubDepartment,
                                        "onUpdate:value": (value) => (model.refSubDepartment = value),
                                        disabled: !model.refDepartment || viewOnly,
                                        list: ({ searchQuery }) => callSubDepartment({ searchQuery, parentId: model.refDepartment.id }),
                                        optLabel: "nameTh",
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) },
                        }),
                    ]}),
                    skeleton: isLoading.value,
                }),
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: putField({ data, fields: [
                        createFieldDate({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}registerDate`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.registerDate"),
                            labelRender: () => (
                                <span>
                                    {iconCheckRegisterDate({ data, model })} {i18n.global.t("model.user.registerDate")}
                                </span>
                            ),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    { value: model.registerDate, "onUpdate:value": (value) => (model.registerDate = value), disabled: viewOnly },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                        }),
                        createFieldInputNumber({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}registerCapital`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.registerCapital"),
                            labelRender: () => (
                                <span>
                                    {iconCheckRegisterCapital({ data, model })} {i18n.global.t("model.user.registerCapital")}
                                </span>
                            ),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: modelRef.registerCapital,
                                        "onUpdate:value": (value) => (modelRef.registerCapital = value),
                                        disabled: viewOnly,
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                        }),
                        createFieldInputNumber({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}shareholdingTh`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.shareholdingTh"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model?.shareholdingTh,
                                        "onUpdate:value": (value) => {
                                            model.shareholdingTh = value > 100 ? 100 : value;
                                        },
                                        disabled: viewOnly,
                                        comma: true,
                                        maxDecimal: 3,
                                        allowPrecision: true,
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                        }),
                    ]}),
                    skeleton: isLoading.value,
                }),
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: putField({ data, fields: [
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}idLine`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.idLine"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.idLine,
                                        "onUpdate:value": (value) => (model.idLine = value),
                                        disabled: viewOnly,
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                        }),
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refEducation`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.refEducation"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refEducation,
                                        "onUpdate:value": (value) => (model.refEducation = value),
                                        disabled: viewOnly,
                                        list: ({ searchQuery }) => callEducation({ searchQuery }),
                                        optLabel: "value",
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) },
                        }),
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refMainOccupation`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.refMainOccupation"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refMainOccupation,
                                        "onUpdate:value": (value) => (model.refMainOccupation = value),
                                        disabled: viewOnly,
                                        list: ({ searchQuery }) => callMainOccupation({ searchQuery }),
                                        optLabel: "value",
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) },
                        }),
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refSecondOccupation`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.refSecondOccupation"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refSecondOccupation,
                                        "onUpdate:value": (value) => (model.refSecondOccupation = value),
                                        disabled: viewOnly,
                                        list: ({ searchQuery }) => callSecondOccupation({ searchQuery }),
                                        optLabel: "value",
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) },
                        }),
                    ]}),
                    skeleton: isLoading.value,
                }),
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: putField({ data, fields: [
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}userRoleRequests`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.role.modelName"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.userRoleRequests,
                                        "onUpdate:value": (value) => (model.userRoleRequests = value),
                                        disabled: viewOnly,
                                        list: ({ searchQuery }) =>  callRole({ searchQuery }).then(list => list.filter(it => !["FILE_MANAGER", "EDIT_LICENSE_STATUS"].includes(it.refRole.name))),
                                        renderLabel: ({ item }) => (
                                            <span>
                                                {item.refRole.desc}
                                            </span>
                                        ),
                                        mode: "multiple",
                                        optKey: "refRole.id",
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                            changeLog: { getValueChildren: ({ record }) => record.refRole?.name },
                        }),
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refRegisterRole`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.refRegisterRole"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refRegisterRole,
                                        "onUpdate:value": (value) => (model.refRegisterRole = value),
                                        disabled: viewOnly,
                                        list: ({ searchQuery }) => callRegisterRole({ searchQuery }),
                                        optLabel: "value",
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) },
                        }),
                    ]}),
                    skeleton: isLoading.value,
                }),
            }),
        ],
    });
};

export const createFieldUsernamePassword = ({ props, data, key, isLoading }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: putField({ data, fields: [
                        createFieldInputPassword({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}password`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.password"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.password,
                                        "onUpdate:value": (value) => (model.password = value),
                                        disabled: viewOnly,
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                        }),
                        createFieldInputPassword({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}passwordConfirm`,
                            colProp: () => ({ xs: 24, md: 12 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.user.passwordConfirm"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.passwordConfirm,
                                        "onUpdate:value": (value) => (model.passwordConfirm = value),
                                        disabled: viewOnly,
                                        onBlur: () => useForm.validate(field.key).catch(() => {}),
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                        }),
                    ]}),
                    skeleton: isLoading.value,
                }),
            }),
        ],
    });
};

export const createFieldTabPerson = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => <Card>{i18n.global.t("views.register.condition")}</Card>,
            },
            createFieldCheckbox({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}acceptCondition`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 1 }, wrapperCol: { xs: 23 }, colon: false }),
                label: () => " ",
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            checked: model.acceptCondition,
                            "onUpdate:checked": (checked) => (model.acceptCondition = checked),
                            disabled: viewOnly,
                            text: () => i18n.global.t("views.register.acceptCondition"),
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
            }),
        ],
    });
};

export const createFieldAddressType = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldRadioGroup({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refSameAddressType`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 20 } }),
                label: () => i18n.global.t("model.user.refSameAddressType"),
                labelRender: () => "",
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.refSameAddressType,
                            "onUpdate:value": (value) => {
                                model.refSameAddressType = value;
                                if (value?.key === "1") {
                                    const temp = model.addresses[0];
                                    model.addresses = [temp];
                                } else if (value?.key === "2") {
                                    model.addresses[1] = createFormAddress();
                                    model.addresses[1].refAddressType = data.addressTypes.find((d) => d.key === "2");
                                }
                            },
                            disabled: viewOnly,
                            list: callSameAddressType,
                            renderLabel: ({ item }) => {
                                return () => (
                                    <span>
                                        {item.value}
                                        {data.addressPersonType}
                                    </span>
                                );
                            },
                            optLabel: "value",
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) },
            }),
        ],
    });
};

export const createFieldRegisterStatus = ({ props, data, key, isLoading }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}statusCard`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                formItemRender: () => {
                    const fields = putField({
                        data,
                        fields: [
                            createFieldTemplateBuilder({
                                colProp: () => ({ xs: 24 }),
                                inputProp: () => ({
                                    useForm: useForm,
                                    fields: putField({ data, fields: [
                                        createFieldSelect({
                                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                            key: `${prefix}refStatus`,
                                            colProp: () => ({ xs: 24, md: 12 }),
                                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                                            label: () => i18n.global.t("model.userRequest.refStatus"),
                                            inputProp: ({ field }) =>
                                                lodash.merge(
                                                    {
                                                        value: model.refStatus,
                                                        "onUpdate:value": value => (model.refStatus = value),
                                                        disabled: viewOnly,
                                                        list: callUserRequestStatus,
                                                        optLabel: "value"
                                                    },
                                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                                ),
                                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.fullNameTh`) }
                                        }),
                                        createFieldSelect({
                                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                            key: `${prefix}refConfirmBy`,
                                            colProp: () => ({ xs: 24, md: 12 }),
                                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                                            label: () => i18n.global.t("model.userRequest.refConfirmBy"),
                                            inputProp: ({ field }) =>
                                                lodash.merge(
                                                    {
                                                        value: model.refConfirmBy,
                                                        "onUpdate:value": value => (model.refConfirmBy = value),
                                                        disabled: viewOnly,
                                                        list: callUser,
                                                        optLabel: "fullNameTh"
                                                    },
                                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                                ),
                                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.fullNameTh`) }
                                        }),
                                        createFieldTextArea({
                                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                            key: `${prefix}remark`,
                                            colProp: () => ({ xs: 24 }),
                                            formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 20 } }),
                                            label: () => i18n.global.t("model.userRequest.remark"),
                                            inputProp: ({ field }) =>
                                                lodash.merge(
                                                    {
                                                        value: model.remark,
                                                        "onUpdate:value": (value) => (model.remark = value),
                                                        disabled: viewOnly,
                                                    },
                                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                                ),
                                        }),
                                    ]})
                                })
                            }),
                        ]
                    });
                    return <CardBorder
                        bodyStyle={{ padding: "10px 24px" }}
                        class="mt-3 border-radius-1"
                        headerSize="small"
                        title={i18n.global.t("model.userRequest.statusCard")}
                    >
                        <TemplateBuilder rowProp={data.rowProp}
                                         useForm={data.useForm}
                                         fields={fields}
                                         skeleton={isLoading.value}
                        ></TemplateBuilder>
                    </CardBorder>;
                }
            },
        ]
    })
}

export const iconCheckBirthDate = ({ data }) => {
    let icon = null;
    if (!data.linkIdCard) {
        return null;
    }
    icon = <CIcon class="text-success" file="check.svg"></CIcon>;
    if ("wait" === data.linkIdCard) {
        icon = <CIcon class="text-danger" file="waiting.svg"></CIcon>;
    } else if (!data.linkIdCard?.birthDate) {
        icon = <CIcon class="text-danger" file="close.svg"></CIcon>;
    }
    return <span>{icon}</span>;
};

export const iconCheckExpireDate = ({ data }) => {
    let icon = null;
    if (!data.linkIdCard) {
        return null;
    }
    icon = <CIcon class="text-success" file="check.svg"></CIcon>;
    if ("wait" === data.linkIdCard) {
        icon = <CIcon class="text-danger" file="waiting.svg"></CIcon>;
    } else if (!data.linkIdCard?.expireDate) {
        icon = <CIcon class="text-danger" file="close.svg"></CIcon>;
    }
    return <span>{icon}</span>;
};

export const iconCheckRegisterCapital = ({ data, model }) => {
    let icon = null;
    if (!data.linkIdCard) {
        return null;
    }
    icon = <CIcon class="text-success" file="check.svg"></CIcon>;
    if ("wait" === data.linkIdCard) {
        icon = <CIcon class="text-danger" file="waiting.svg"></CIcon>;
    } else if (data.linkIdCard?.registerCapital != model.registerCapital) {
        icon = <CIcon class="text-danger" file="close.svg"></CIcon>;
    }
    return <Tooltip title={data.linkIdCard?.registerCapital}>{icon}</Tooltip>;
};

export const iconCheckRegisterDate = ({ data, model }) => {
    let icon = null;
    if (!data.linkIdCard) {
        return null;
    }
    icon = <CIcon class="text-success" file="check.svg"></CIcon>;
    if ("wait" === data.linkIdCard) {
        icon = <CIcon class="text-danger" file="waiting.svg"></CIcon>;
    } else {
        const registerDateTh = data.linkIdCard?.registerDate;
        if (registerDateTh) {
            const year = parseInt(registerDateTh.substr(0, 4)) - 543;
            const dbdRegisterDate = moment(year + registerDateTh.slice(4), "YYYYMMDD");
            const registerDate = moment(model.registerDate, "YYYY-MM-DD");
            const isSame = dbdRegisterDate?.isSame(registerDate);
            if (isSame) {
                return <Tooltip title={data.linkIdCard?.registerDate}>{icon}</Tooltip>;
            }
        }
        icon = <CIcon class="text-danger" file="close.svg"></CIcon>;
    }
    return <Tooltip title={data.linkIdCard?.registerDate}>{icon}</Tooltip>;
};
